// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { SidebarSelection } from './types';

type Translate = (key: string, params?: Record<string, string | number>) => string;

export function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    (crypto as Crypto).getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

export function addOneDay(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export const normalizeGameId = (value?: string | null) => (value ?? '').trim().toLowerCase();

/**
 * Label for a round in the sidebar. Rounds are usually left unnamed, and the
 * course says far more than a positional "Round 3" does — so fall back to the
 * course and only use the position when neither is set.
 */
export const roundDisplayLabel = (
  round: { name?: string; courseName?: string } | undefined,
  index: number,
  t: Translate
): string => {
  const name = (round?.name ?? '').trim();
  if (name) return name;
  const courseName = (round?.courseName ?? '').trim();
  if (courseName) return courseName;
  return index >= 0 ? t('tourEdit.roundFallbackNumbered', { number: index + 1 }) : t('tourEdit.roundFallbackName');
};

/**
 * Human title for the currently edited section. Shared by the desktop editor
 * header and the mobile detail header so both surfaces name a step identically.
 */
export function sectionTitle(
  step: SidebarSelection,
  rounds: ReadonlyArray<{ id: string; name?: string }>,
  t: Translate
): string {
  switch (step.kind) {
    case 'tour-name':
      return t('tourEdit.sidebarTourBasics');
    case 'tour-players':
      return t('tourEdit.playersTitle');
    case 'tour-games':
      return t('tourEdit.sidebarGames');
    case 'round-settings': {
      const round = 'roundId' in step ? rounds.find((r) => r.id === step.roundId) : undefined;
      return round?.name ? `${round.name} — ${t('tourEdit.sidebarRoundSettings')}` : t('tourEdit.roundSettingsTitle');
    }
    case 'round-players': {
      const round = 'roundId' in step ? rounds.find((r) => r.id === step.roundId) : undefined;
      return round?.name ? `${round.name} — ${t('tourEdit.playersTitle')}` : t('tourEdit.roundPlayersTitle');
    }
    case 'round-games': {
      const round = 'roundId' in step ? rounds.find((r) => r.id === step.roundId) : undefined;
      return round?.name ? `${round.name} — ${t('tourEdit.sidebarGames')}` : t('tourEdit.roundGamesTitle');
    }
    default:
      return '';
  }
}

export const areStringListsEqual = (left: ReadonlyArray<string>, right: ReadonlyArray<string>) => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};
